/* eslint-disable no-param-reassign */

import _ from 'lodash';
import { Action, Dispatch } from 'redux';
import { updateAssetAccessLog } from '../api/vault/logs';
import {
  IAssetAccessLogHit,
  IAssetAccessLog,
  searchAssetAccessLogs,
} from '../api/vault/searchEngine';
import {
  IOrg,
  getOrgs,
} from '../api/idm';
import { IAppState } from './reducers';

export interface IAssetAccessLogShim extends IAssetAccessLog {
  id: string; // note: necessary for table row
  stepFailed: string;
  ticket?: string;
  retried?: boolean;
}

const shimLogFromHit = (hit: IAssetAccessLogHit): IAssetAccessLogShim => {
  let log = {
    ...hit._source,
    id: hit._id,
  } as IAssetAccessLogShim;
  if (log.actionType === 'Failure' && Array.isArray(log.metadata)) {
    const stepFailed = log.metadata.find(({ label, value }) => label === 'type');
    const ticket = log.metadata.find(({ label, value }) => label === 'ticketUrl');
    const retry = log.metadata.find(({ label, value }) => label === 'retried');
    log = {
      ...log,
      stepFailed: stepFailed.value,
    };
    if (ticket) {
      log = {
        ...log,
        ticket: ticket.value,
      };
    }
    if (retry) {
      log = {
        ...log,
        retried: !!retry,
      };
    }
  }
  return log;
};

export interface IState {
  isLoading: boolean;
  orgs: Array<IOrg>;
  org: string;
  logs: Array<IAssetAccessLogShim>;
  startDate: Date;
  endDate: Date;
  isEndDateOpen: boolean;
}

export enum ActionType {
  UpdateOrgs = 'AdminDashboard.UpdateOrgs',
  SelectOrg = 'AdeminDashboard.SelectOrg',
  UpdateAssetAccessLogs = 'AdminDashboard.UpdateAssetAccessLogs',
  UpdateStartDate = 'AdminDashboard.UpdateStartDate',
  UpdateEndDate = 'AdminDashboard.UpdateEndDate',
  ToggleEndDate = 'AdminDashboard.ToggleEndDate',
  ToggleLoading = 'AdminDashboard.ToggleLoading',
  Reset = 'AdminDashboard.Reset',
}

export interface IAdminDashboardAction extends Action<ActionType> {
  payload?: any;
}

// note: initialize state with selected dates of previous week
const INITIAL_STATE: IState = {
  isLoading: false,
  orgs: [],
  org: null,
  logs: [],
  startDate: new Date(Date.now() - (3600 * 1000 * 24 * 7)),
  endDate: new Date(),
  isEndDateOpen: false,
};

export default (state: IState = INITIAL_STATE, action: IAdminDashboardAction) => {
  switch (action.type) {
    case ActionType.UpdateOrgs:
      return {
        ...state,
        orgs: action.payload,
      };
    case ActionType.SelectOrg:
      return {
        ...state,
        org: action.payload,
      };
    case ActionType.UpdateAssetAccessLogs:
      return {
        ...state,
        logs: action.payload,
        isLoading: false,
      };
    case ActionType.UpdateStartDate:
      return {
        ...state,
        startDate: action.payload,
      };
    case ActionType.UpdateEndDate:
      return {
        ...state,
        endDate: action.payload,
      };
    case ActionType.ToggleEndDate:
      return {
        ...state,
        isEndDateOpen: action.payload,
      };
    case ActionType.ToggleLoading:
      return {
        ...state,
        isLoading: true,
      };
    case ActionType.Reset:
      return INITIAL_STATE;
    default:
      return state;
  }
};

export const loadOrgs = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const response = await getOrgs();
  dispatch({
    type: ActionType.UpdateOrgs,
    payload: _.sortBy(response.data.data, 'name'),
  });
};

export const queryAssetAccessLogs = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const {
    adminDashboard: {
      startDate: fromDate,
      endDate: toDate,
      org,
    },
  } = state;
  dispatch({
    type: ActionType.ToggleLoading,
  });
  const filters = [['actionType', ['Failure']]];
  if (org) {
    filters.push(['orgName', [org]]);
  }
  const response = await searchAssetAccessLogs({
    queryString: '',
    filters,
    fromDate,
    toDate,
    size: 10000,
    page: 1,
  });
  const { data: { data: { hits: { hits } } } } = response;
  const logs = hits.map((hit) => shimLogFromHit(hit));
  dispatch({
    type: ActionType.UpdateAssetAccessLogs,
    payload: _.orderBy(logs, 'actionDate', 'desc'),
  });
};

export const selectOrg = (orgName: string) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.SelectOrg,
    payload: orgName,
  });
  queryAssetAccessLogs()(dispatch, getState);
};

export const setStartDate = (date: Date) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.UpdateStartDate,
    payload: date,
  });
  queryAssetAccessLogs()(dispatch, getState);
};

export const setEndDate = (date: Date) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.UpdateEndDate,
    payload: date,
  });
  queryAssetAccessLogs()(dispatch, getState);
};

export const startDateClose = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  // Check to query/date range change
  dispatch({
    type: ActionType.ToggleEndDate,
    payload: true,
  });
};

export const toggleEndDate = (open: boolean) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  if (!open) {
    // Check to query/date range change
  }
  dispatch({
    type: ActionType.ToggleEndDate,
    payload: open,
  });
};

export const updateJiraTicket = (log: IAssetAccessLogShim, value: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const { adminDashboard: { logs } } = state;
  const pos = logs.findIndex(({ id }) => id === log.id);
  const source = log.metadata.find(({ label }) => label === 'ticketUrl');
  // note: update metadata reference
  if (source) {
    source.value = value;
  } else {
    log.metadata.push({
      label: 'ticketUrl',
      value,
    });
  }
  // note: update ticket shim
  log.ticket = value;
  // note: update log in collection
  logs.splice(pos, 1, log);
  await updateAssetAccessLog(log.id, log as IAssetAccessLog);
  dispatch({
    type: ActionType.UpdateAssetAccessLogs,
    payload: logs,
  });
};

export const setRetried = (log: IAssetAccessLogShim) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const { adminDashboard: { logs } } = state;
  const pos = logs.findIndex(({ id }) => id === log.id);
  const source = log.metadata.find(({ label }) => label === 'retried');
  // note: update metadata
  if (!source) {
    log.metadata.push({
      label: 'retried',
      value: 'true',
    });
  }
  // note: update retry shim
  log.retried = true;
  // note: update log in collection
  logs.splice(pos, 1, log);
  dispatch({
    type: ActionType.UpdateAssetAccessLogs,
    payload: [...logs],
  });
  await updateAssetAccessLog(log.id, log as IAssetAccessLog);
};
